// Domain models (documented as JS objects; TypeScript-like shapes below)
//
// WorkoutBlock  { id, name, work, rest, rounds, color, exercises? }
// WorkoutRoutine { id, name, blocks: WorkoutBlock[], countdown?, createdAt }
// WorkoutPhase  { type, label, duration, blockIndex, round, totalRounds, color,
//                 startSound?, endSound?, isRoundEnd?, next?, index? }
// PresetRoutine { id, name, level, category, calories, accent, blocks }
// AudioSettings { enabled, countdownVoice, volume }
// AppSettings   { audio, theme, keepAwake, autoStart, countdownBefore, countdownSeconds }

let _seq = 0;
export const uid = (prefix = 'id') =>
  `${prefix}_${Date.now().toString(36)}_${(_seq++).toString(36)}`;

export const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

export const formatTime = (totalSeconds) => {
  const s = Math.max(0, Math.round(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
};

export const createBlock = (partial = {}) => ({
  id: uid('block'),
  name: 'New Exercise',
  work: 20,
  rest: 10,
  rounds: 8,
  color: '#38BDF8',
  ...partial,
});

export const createRoutine = (partial = {}) => ({
  id: uid('routine'),
  name: 'My Routine',
  blocks: [createBlock()],
  countdown: 3,
  createdAt: new Date().toISOString(),
  ...partial,
});

// Total duration in seconds for a routine.
export const calcTotalDuration = (routine, includeCountdown = false) => {
  if (!routine || !routine.blocks) return 0;
  const base = routine.blocks.reduce(
    (sum, b) => sum + b.rounds * (Number(b.work) + Number(b.rest)),
    0
  );
  return base + (includeCountdown ? Number(routine.countdown || 0) : 0);
};

export const totalRounds = (routine) =>
  routine.blocks.reduce((sum, b) => sum + Number(b.rounds), 0);

// Serialization helpers
export const serializeRoutine = (routine) => JSON.stringify(routine);
export const deserializeRoutine = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

// Expand a routine into a flat list of phases for the timer engine.
export const generatePhases = (routine, options = {}) => {
  const phases = [];
  const countdownBefore =
    options.countdownBefore ?? true;
  const countdownSeconds =
    options.countdownSeconds ?? routine.countdown ?? 3;

  if (countdownBefore && countdownSeconds > 0) {
    phases.push({
      type: 'countdown',
      label: 'Get Ready',
      duration: countdownSeconds,
      blockIndex: 0,
      round: 0,
      totalRounds: 0,
    });
  }

  const blocks = routine.blocks || [];
  blocks.forEach((block, bi) => {
    const rounds = Number(block.rounds) || 1;
    for (let r = 1; r <= rounds; r++) {
      const label =
        block.exercises && block.exercises.length
          ? block.exercises[(r - 1) % block.exercises.length]
          : block.name;

      const workPhase = {
        type: 'work',
        label,
        duration: Number(block.work),
        blockIndex: bi,
        round: r,
        totalRounds: rounds,
        color: block.color,
        startSound: 'workStart',
        endSound: 'workEnd',
      };
      phases.push(workPhase);

      const isLastRoundOfBlock = r === rounds;
      const isLastBlock = bi === blocks.length - 1;
      const isLastPhaseOverall = isLastRoundOfBlock && isLastBlock;

      if (Number(block.rest) > 0 && !isLastPhaseOverall) {
        phases.push({
          type: isLastRoundOfBlock ? 'restBlock' : 'rest',
          label: isLastRoundOfBlock ? 'Block Rest' : 'Rest',
          duration: Number(block.rest),
          blockIndex: bi,
          round: r,
          totalRounds: rounds,
          startSound: 'restStart',
          isRoundEnd: true,
        });
      } else {
        workPhase.isRoundEnd = true;
      }
    }
  });

  phases.forEach((p, i) => {
    p.index = i;
    p.next = phases[i + 1] ? phases[i + 1].label : null;
  });

  return phases;
};
